import axiosInstance from "@/common/interceptors/axiosInstance";
import type {
  IntelligenceDashboardResponse,
  IntelligenceFeatureFlag,
  IntelligenceFreshnessRow,
  IntelligenceInvoiceExportResponse,
  IntelligenceLineageRow,
  IntelligenceObservabilitySummary,
  IntelligenceRevenueSummary,
  IntelligenceTierConfig,
  IntelligenceUsageResponse,
} from "@/types/intelligence";

type ApiResponse<T> = { data: T };

const getFilenameFromDisposition = (value?: string) => {
  if (!value) return undefined;
  const match = /filename\*?=(?:UTF-8'')?["']?([^"';\n]+)/i.exec(value);
  return match ? decodeURIComponent(match[1]) : undefined;
};

export const fetchIntelligenceDashboard = async (): Promise<IntelligenceDashboardResponse> => {
  const { data } = await axiosInstance.get<ApiResponse<IntelligenceDashboardResponse>>(
    "/v1/admin/intelligence/dashboard"
  );
  return data.data;
};

export const fetchIntelligenceUsage = async (days = 30): Promise<IntelligenceUsageResponse> => {
  const { data } = await axiosInstance.get<ApiResponse<IntelligenceUsageResponse>>(
    `/v1/admin/intelligence/usage?days=${days}`
  );
  return data.data;
};

export const fetchIntelligenceObservability = async (): Promise<IntelligenceObservabilitySummary> => {
  const { data } = await axiosInstance.get<ApiResponse<IntelligenceObservabilitySummary>>(
    "/v1/admin/intelligence/observability"
  );
  return data.data;
};

export const fetchIntelligenceRevenue = async (month: string): Promise<IntelligenceRevenueSummary> => {
  const { data } = await axiosInstance.get<ApiResponse<IntelligenceRevenueSummary>>(
    `/v1/admin/intelligence/revenue?month=${month}`
  );
  return data.data;
};

export const exportIntelligenceInvoices = async (
  month: string
): Promise<IntelligenceInvoiceExportResponse> => {
  const response = await axiosInstance.get<Blob>(
    `/v1/admin/intelligence/invoices/export?month=${month}`,
    {
      responseType: "blob",
    }
  );

  return {
    blob: response.data,
    filename: getFilenameFromDisposition(response.headers["content-disposition"]),
  };
};

export const fetchIntelligenceTiers = async (): Promise<IntelligenceTierConfig[]> => {
  const { data } = await axiosInstance.get<ApiResponse<IntelligenceTierConfig[]>>(
    "/v1/admin/intelligence/tiers"
  );
  return data.data;
};

export const fetchIntelligenceFeatureFlags = async (): Promise<IntelligenceFeatureFlag[]> => {
  const { data } = await axiosInstance.get<ApiResponse<IntelligenceFeatureFlag[]>>(
    "/v1/admin/intelligence/feature-flags"
  );
  return data.data;
};

export const fetchIntelligenceFreshness = async (): Promise<IntelligenceFreshnessRow[]> => {
  const { data } = await axiosInstance.get<ApiResponse<IntelligenceFreshnessRow[]>>(
    "/v1/admin/intelligence/freshness"
  );
  return data.data;
};

export const fetchIntelligenceLineage = async (
  datasetName?: string,
  limit = 50
): Promise<IntelligenceLineageRow[]> => {
  const query = new URLSearchParams();
  if (datasetName) query.set("dataset_name", datasetName);
  query.set("limit", String(limit));
  const { data } = await axiosInstance.get<ApiResponse<IntelligenceLineageRow[]>>(
    `/v1/admin/intelligence/lineage?${query.toString()}`
  );
  return data.data;
};
